import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Button, Center, CopyButton, Group, Loader, Popover, Select, SegmentedControl, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, agendaApi } from '../../api';
import { formatDate, formatDateTime, todayIso } from '../../format';
import { PageHeader, Section } from '../../ui/Section';
import { type TodoItem, flatten, layoutWeek, projectColors, runsOn } from './agenda';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const iso = (d: Date): string => d.toISOString().slice(0, 10);
const utc = (s: string): Date => new Date(`${s}T00:00:00Z`);
const add = (s: string, n: number): string => { const d = utc(s); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
/** Monday of the week containing the day. */
const monday = (s: string): string => add(s, -((utc(s).getUTCDay() + 6) % 7));

/**
 * The schedule as a calendar: month or week, each task one bar over the days
 * it runs (as desk calendars draw them), milestones as ◆, one colour per
 * project. Below it the subscription links
 * that bring the same into Nextcloud, Apple Calendar or Thunderbird.
 */
export function CalendarPage({ projectId: fixed }: { projectId?: string } = {}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<{ ownerCodes: string[] }>(['me']);
  const codes = me?.ownerCodes ?? [];
  const data = useQuery({ queryKey: ['agenda'], queryFn: agendaApi.list });
  const [mode, setMode] = useState<'month' | 'week'>('month');
  const [who, setWho] = useState<'mine' | 'all'>(codes.length ? 'mine' : 'all');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState(todayIso());

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Der Kalender konnte nicht geladen werden.</Alert>;
  const today = todayIso();
  // Colours follow the full project list, so a project keeps its colour when filtering.
  const colors = projectColors(data.data.map((p) => p.projectId));
  const items = flatten(data.data).filter((t) => (who === 'all' || t.mine) && (fixed ? t.projectId === fixed : !projectId || t.projectId === projectId));

  const first = mode === 'month' ? monday(`${anchor.slice(0, 7)}-01`) : monday(anchor);
  const weeks = mode === 'month' ? 6 : 1;
  const days = [...Array(weeks * 7).keys()].map((i) => add(first, i));
  const month = anchor.slice(0, 7);
  const step = (dir: number): void => {
    if (mode === 'week') setAnchor(add(anchor, dir * 7));
    else { const d = utc(`${month}-01`); d.setUTCMonth(d.getUTCMonth() + dir); setAnchor(iso(d)); }
  };
  const title = mode === 'month'
    ? utc(`${month}-01`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : `${formatDate(days[0])} – ${formatDate(days[6])}`;

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        {!fixed && <PageHeader title="Kalender – alle Projekte" description="Die Vorgänge aller Terminpläne als Balken über ihre Tage, Meilensteine als ◆. Ein Klick öffnet den Terminplan des Projekts." />}
        <Group gap="sm" wrap="wrap">
          <Group gap={4}>
            <ActionIcon variant="default" aria-label="zurück" onClick={() => step(-1)}>‹</ActionIcon>
            <Button size="xs" variant="default" onClick={() => setAnchor(today)}>Heute</Button>
            <ActionIcon variant="default" aria-label="weiter" onClick={() => step(1)}>›</ActionIcon>
          </Group>
          <Text fw={600} w={200}>{title}</Text>
          <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as 'month' | 'week')} data={[{ value: 'month', label: 'Monat' }, { value: 'week', label: 'Woche' }]} />
          <SegmentedControl size="xs" value={who} onChange={(v) => setWho(v as 'mine' | 'all')} data={[{ value: 'mine', label: 'Meine' }, { value: 'all', label: 'Alle' }]} />
          {!fixed && <Select size="xs" placeholder="alle Projekte" clearable w={260} value={projectId} onChange={setProjectId} data={data.data.map((p) => ({ value: p.projectId, label: `${p.number} – ${p.name}` }))} />}
        </Group>
        {who === 'mine' && codes.length === 0 && <Alert color="yellow" py={6}>Für „Meine“ im Profil das eigene Kürzel eintragen („Meine Kürzel im Terminplan“).</Alert>}

        <div style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {WEEKDAYS.map((d) => <Text key={d} size="xs" fw={600} c="dimmed" p={4} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>{d}</Text>)}
          </div>
          {[...Array(weeks).keys()].map((w) => (
            <WeekRow
              key={w}
              weekStart={days[w * 7]!}
              items={items}
              colors={colors}
              month={mode === 'month' ? month : null}
              today={today}
              maxLanes={mode === 'month' ? 4 : 40}
              minHeight={mode === 'month' ? 118 : 420}
              onOpen={(t) => navigate(`/projekte/${t.projectId}/termine`)}
            />
          ))}
        </div>

        {/* Subscription links span all projects; they live on the cross-project calendar. */}
        {!fixed && <FeedsSection />}
      </Stack>
    </div>
  );
}

const LANE = 19;
const HEAD = 20;

/**
 * One week of the calendar: day numbers on top, the tasks as bars across the
 * days they run - one bar per task, as in a desk calendar, never a copy per
 * day. What does not fit shows as "+n" on the day, with the full list on click.
 */
function WeekRow({ weekStart, items, colors, month, today, maxLanes, minHeight, onOpen }: {
  weekStart: string;
  items: TodoItem[];
  colors: Map<string, string>;
  month: string | null;
  today: string;
  maxLanes: number;
  minHeight: number;
  onOpen: (t: TodoItem) => void;
}): JSX.Element {
  const days = [...Array(7).keys()].map((i) => add(weekStart, i));
  const { bars, lanes } = layoutWeek(items, weekStart);
  const shown = Math.min(lanes, maxLanes);
  const hiddenOn = (col: number): TodoItem[] => bars.filter((b) => b.lane >= maxLanes && b.col <= col && col < b.col + b.span).map((b) => b.item);
  const height = Math.max(minHeight, HEAD + shown * LANE + (lanes > maxLanes ? 18 : 0) + 6);
  return (
    <div style={{ position: 'relative', height, borderBottom: '1px solid var(--mantine-color-default-border)' }}>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {days.map((day, col) => {
          const weekend = col >= 5;
          const other = month !== null && day.slice(0, 7) !== month;
          const hidden = hiddenOn(col);
          return (
            <div key={day} style={{ borderRight: col < 6 ? '1px solid var(--mantine-color-default-border)' : undefined, background: weekend ? 'var(--mantine-color-default-hover)' : undefined, padding: '2px 4px', position: 'relative', opacity: other ? 0.45 : 1 }}>
              <Text size="xs" c="dimmed">
                <span className={day === today ? 'pst-today' : undefined}>{Number(day.slice(8))}</span>
                {day.slice(8) === '01' || month === null ? ` ${utc(day).toLocaleDateString('de-DE', { month: 'short', timeZone: 'UTC' })}` : ''}
              </Text>
              {hidden.length > 0 && (
                <Popover withinPortal position="bottom" shadow="md">
                  <Popover.Target>
                    <Text size="xs" c="dimmed" style={{ position: 'absolute', bottom: 2, left: 4, cursor: 'pointer' }}>+{hidden.length} weitere</Text>
                  </Popover.Target>
                  <Popover.Dropdown maw={340}>
                    <Stack gap={3}>
                      <Text size="xs" fw={600}>{formatDate(day)}</Text>
                      {items.filter((t) => (t.milestone ? t.start === day : runsOn(t, day))).map((t) => (
                        <Bar key={t.id} item={t} color={colors.get(t.projectId) ?? 'brand'} fromBefore={false} toAfter={false} onOpen={() => onOpen(t)} />
                      ))}
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
              )}
            </div>
          );
        })}
      </div>
      {bars.filter((b) => b.lane < maxLanes).map((b) => (
        <div key={b.item.id} style={{ position: 'absolute', top: HEAD + b.lane * LANE, left: `calc(${(b.col / 7) * 100}% + ${b.fromBefore ? 0 : 3}px)`, width: `calc(${(b.span / 7) * 100}% - ${(b.fromBefore ? 0 : 3) + (b.toAfter ? 0 : 3)}px)`, height: LANE - 3 }}>
          <Bar item={b.item} color={colors.get(b.item.projectId) ?? 'brand'} fromBefore={b.fromBefore} toAfter={b.toAfter} onOpen={() => onOpen(b.item)} />
        </div>
      ))}
    </div>
  );
}

/** A task as a bar: rounded where it begins and ends, straight where it runs on beyond the week. */
function Bar({ item: t, color, fromBefore, toAfter, onOpen }: { item: TodoItem; color: string; fromBefore: boolean; toAfter: boolean; onOpen: () => void }): JSX.Element {
  const done = t.status === 'done';
  const range = t.milestone ? formatDate(t.start) : `${formatDate(t.start)} – ${formatDate(t.finish)}`;
  return (
    <Tooltip label={`${t.projectNumber} ${t.code} ${t.name} · ${range}${t.owner ? ` · ${t.owner}` : ''}`} multiline w={320} openDelay={300}>
      <div
        role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => e.key === 'Enter' && onOpen()}
        style={{
          height: '100%', cursor: 'pointer', fontSize: 11, lineHeight: '16px', padding: '0 5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          background: t.milestone ? 'transparent' : `var(--mantine-color-${color}-light)`,
          borderLeft: fromBefore || t.milestone ? undefined : `3px solid var(--mantine-color-${color}-6)`,
          borderRadius: `${fromBefore ? 0 : 4}px ${toAfter ? 0 : 4}px ${toAfter ? 0 : 4}px ${fromBefore ? 0 : 4}px`,
          color: t.milestone ? `var(--mantine-color-${color}-8)` : undefined,
          fontWeight: t.milestone ? 600 : undefined,
          opacity: done ? 0.5 : 1, textDecoration: done ? 'line-through' : undefined,
        }}
      >
        {t.milestone ? '◆ ' : fromBefore ? '◂ ' : ''}{t.projectNumber} {t.code} {t.name}
      </div>
    </Tooltip>
  );
}

/** The subscription links: create (the URL shows once), see when last fetched, revoke. */
function FeedsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const feeds = useQuery({ queryKey: ['feeds'], queryFn: agendaApi.feeds });
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [label, setLabel] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => agendaApi.createFeed(scope, label.trim() || (scope === 'mine' ? 'Meine Aufgaben' : 'Alle Aufgaben')),
    onSuccess: (r) => { setFresh(r.url); setLabel(''); },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['feeds'] }),
  });
  const remove = useMutation({ mutationFn: (id: string) => agendaApi.removeFeed(id), onSettled: () => queryClient.invalidateQueries({ queryKey: ['feeds'] }) });
  const error = create.error ?? remove.error;
  return (
    <Section
      title="Im eigenen Kalender abonnieren"
      description="Ein geheimer Link im iCalendar-Format: In Nextcloud (Kalender → „+ Neues Abonnement“), Apple Kalender („Ablage → Neues Kalenderabonnement“) oder Thunderbird („Neuer Kalender → Im Netzwerk“) eintragen. Der Kalender aktualisiert sich selbst, etwa stündlich; geändert wird in PST. Wer den Link hat, sieht die Aufgaben – bei Verlust widerrufen."
    >
      {error && <Alert color="red" py={6} withCloseButton onClose={() => { create.reset(); remove.reset(); }}>{error instanceof ApiError ? error.message : 'Fehlgeschlagen.'}</Alert>}
      {fresh && (
        <Alert color="green" py={6} withCloseButton onClose={() => setFresh(null)} title="Link erstellt – nur jetzt sichtbar">
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>{fresh}</Text>
            <CopyButton value={fresh}>{({ copied, copy }) => <Button size="compact-xs" onClick={copy} color={copied ? 'green' : undefined}>{copied ? 'kopiert' : 'Kopieren'}</Button>}</CopyButton>
          </Group>
        </Alert>
      )}
      {(feeds.data ?? []).length > 0 && (
        <Table verticalSpacing={3}>
          <Table.Tbody>
            {feeds.data!.map((f) => (
              <Table.Tr key={f.id}>
                <Table.Td><Text size="sm">{f.label}</Text></Table.Td>
                <Table.Td w={120}><Text size="xs" c="dimmed">{f.scope === 'mine' ? 'meine Aufgaben' : 'alle Aufgaben'}</Text></Table.Td>
                <Table.Td w={200}><Text size="xs" c="dimmed">erstellt {formatDate(f.createdAt)}</Text></Table.Td>
                <Table.Td w={230}><Text size="xs" c="dimmed">{f.lastUsedAt ? `zuletzt abgerufen ${formatDateTime(f.lastUsedAt)}` : 'noch nicht abgerufen'}</Text></Table.Td>
                <Table.Td w={110}><Button size="compact-xs" variant="subtle" color="red" onClick={() => { if (window.confirm(`Link „${f.label}“ widerrufen? Abonnierte Kalender bekommen danach nichts mehr.`)) remove.mutate(f.id); }}>Widerrufen</Button></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Group gap="xs" align="flex-end" wrap="wrap">
          <SegmentedControl size="xs" value={scope} onChange={(v) => setScope(v as 'mine' | 'all')} data={[{ value: 'mine', label: 'Meine Aufgaben' }, { value: 'all', label: 'Alle Aufgaben' }]} />
          <TextInput size="xs" placeholder="Name, z. B. iPhone" value={label} onChange={(e) => setLabel(e.currentTarget.value)} w={200} aria-label="Name des Links" />
          <Button size="xs" type="submit" loading={create.isPending}>Link erstellen</Button>
        </Group>
      </form>
    </Section>
  );
}
